<template>
  <section class="home-summary-card">
    <header class="home-summary-card__header">
      <div class="home-summary-card__title-group">
        <div class="home-summary-card__icon" :class="`home-summary-card__icon--${tone}`">
          <BaseIcon :name="icon" size="16" />
        </div>
        <h3 class="home-summary-card__title">{{ title }}</h3>
        <span v-if="!loading && count != null" class="home-summary-card__count">
          {{ count }}
        </span>
      </div>
      <slot name="actions" />
    </header>

    <div
      class="home-summary-card__body"
      :class="{ 'home-summary-card__body--flush': flush && !loading && !isEmpty }"
    >
      <div v-if="loading" class="home-summary-card__loading">
        <LoadingSpinner />
      </div>

      <EmptyState
        v-else-if="isEmpty"
        size="sm"
        :icon="emptyIcon || icon"
        :title="emptyTitle"
        :description="emptyDescription"
      />

      <slot v-else />
    </div>

    <footer
      v-if="(viewAllRoute || $slots.footer) && (alwaysShowViewAll || (!loading && !isEmpty))"
      class="home-summary-card__footer"
    >
      <slot name="footer">
        <router-link :to="viewAllRoute" class="home-summary-card__view-all">
          <span>{{ viewAllLabel }}</span>
          <span v-if="hasMore" class="home-summary-card__more">
            +{{ count - shownCount }}
          </span>
          <BaseIcon name="arrow-right" size="12" />
        </router-link>
      </slot>
    </footer>
  </section>
</template>

<script>
  import { computed } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';

  export default {
    name: 'HomeSummaryCard',

    components: { BaseIcon, LoadingSpinner, EmptyState },

    props: {
      title: { type: String, required: true },
      icon: { type: String, required: true },
      tone: {
        type: String,
        default: 'brand',
        validator: (v) => ['brand', 'amber', 'green', 'sky', 'violet'].includes(v),
      },
      loading: { type: Boolean, default: false },
      isEmpty: { type: Boolean, default: false },
      emptyTitle: { type: String, default: '' },
      emptyDescription: { type: String, default: '' },
      emptyIcon: { type: String, default: null },
      count: { type: Number, default: null },
      shownCount: { type: Number, default: 0 },
      viewAllRoute: { type: [String, Object], default: null },
      viewAllLabel: { type: String, default: '' },
      alwaysShowViewAll: { type: Boolean, default: false },
      flush: { type: Boolean, default: false },
    },

    setup(props) {
      const hasMore = computed(
        () => props.count != null && props.count > props.shownCount
      );
      return { hasMore };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .home-summary-card {
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-radius: 0.75rem;
    border: 1px solid var(--slate-200);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.04);
    overflow: hidden;
    min-height: 280px;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.875rem 1rem;
      border-bottom: 1px solid var(--slate-100);
    }

    &__title-group {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 0;
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 0.5rem;
      flex-shrink: 0;

      &--brand {
        background: var(--brand-50);
        color: var(--brand-600);
      }
      &--amber {
        background: var(--amber-50);
        color: var(--amber-600);
      }
      &--green {
        background: var(--green-50);
        color: var(--green-600);
      }
      &--sky {
        background: var(--sky-50, #f0f9ff);
        color: var(--sky-600, #0284c7);
      }
      &--violet {
        background: var(--violet-50, #f5f3ff);
        color: var(--violet-600, #7c3aed);
      }
    }

    &__title {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-900);
      @include ellipsis(1);
    }

    &__count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.375rem;
      height: 1.375rem;
      padding: 0 0.4375rem;
      border-radius: 9999px;
      background: var(--slate-100);
      color: var(--slate-700);
      font-size: 0.6875rem;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }

    &__body {
      flex: 1;
      padding: 0.5rem 0.5rem 0.25rem;
      min-height: 0;

      &--flush {
        padding: 0;
      }
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2.5rem 0;
    }

    &__footer {
      border-top: 1px solid var(--slate-100);
      padding: 0.625rem 1rem;
      display: flex;
      justify-content: flex-end;
    }

    &__view-all {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--brand-600);
      text-decoration: none;
      padding: 0.25rem 0.5rem;
      border-radius: 0.375rem;
      transition: all 0.15s ease;

      @include hover {
        background: var(--brand-50);
        color: var(--brand-700);
      }
    }

    &__more {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0 0.4375rem;
      height: 1.125rem;
      border-radius: 9999px;
      background: var(--brand-100);
      color: var(--brand-700);
      font-size: 0.6875rem;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }
  }
</style>
